<script setup>
/**
 * Campo de uma variável de template no disparo/envio WhatsApp.
 *
 * É um input de texto livre (permite misturar texto + variáveis) com um botão
 * "Variável" que abre um dropdown com as variáveis de contato. Clicar numa
 * variável insere o token Liquid ({{ contact.x }}) na posição do cursor, em um
 * único clique, e fecha o dropdown.
 *
 * O token é resolvido POR CONTATO no backend (Liquidable/ContactDrop). Aqui só
 * montamos o texto. Ver dashboard/helper/contactTemplateVariables.js.
 */
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  CONTACT_VARIABLES,
  buildContactToken,
  buildCustomAttributeVariables,
} from 'dashboard/helper/contactTemplateVariables';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const { t } = useI18n();

const rootRef = ref(null);
const inputRef = ref(null);
const isOpen = ref(false);
// Guarda a posição do cursor para inserir o token no lugar certo.
const cursor = ref({ start: null, end: null });

// Atributos nativos do contato (lista fixa).
const nativeOptions = computed(() =>
  CONTACT_VARIABLES.map(variable => ({
    ...variable,
    label: t(variable.labelKey),
  }))
);

// Campos personalizados de contato cadastrados na conta. Carregados pelo
// componente pai (WhatsAppTemplateParser dispara attributes/get); aqui só lemos.
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const customOptions = computed(() =>
  buildCustomAttributeVariables(contactAttributes.value)
);

const rememberCursor = () => {
  const el = inputRef.value;
  if (!el) return;
  cursor.value = { start: el.selectionStart, end: el.selectionEnd };
};

const handleInput = event => {
  emit('update:modelValue', event.target.value);
  rememberCursor();
};

const handleBlur = event => {
  rememberCursor();
  emit('blur', event);
};

const closeDropdown = () => {
  isOpen.value = false;
};

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
    return;
  }
  rememberCursor();
  isOpen.value = true;
};

// Insere o token da variável na posição do cursor (em 1 clique) e fecha.
const insertVariable = variable => {
  const token = buildContactToken(variable.attr);
  const current = props.modelValue ?? '';
  const start = cursor.value.start ?? current.length;
  const end = cursor.value.end ?? current.length;
  const next = `${current.slice(0, start)}${token}${current.slice(end)}`;

  emit('update:modelValue', next);
  closeDropdown();

  // Reposiciona o cursor logo após o token inserido.
  nextTick(() => {
    const el = inputRef.value;
    if (!el) return;
    const position = start + token.length;
    el.focus();
    el.setSelectionRange(position, position);
    cursor.value = { start: position, end: position };
  });
};

const onDocumentClick = event => {
  if (isOpen.value && rootRef.value && !rootRef.value.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => document.addEventListener('click', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));
</script>

<template>
  <div ref="rootRef" class="relative flex items-start gap-2">
    <input
      ref="inputRef"
      :value="modelValue"
      type="text"
      :placeholder="placeholder"
      class="block w-full h-10 text-sm transition-all duration-500 ease-in-out border-0 border-none rounded-lg outline outline-1 reset-base !mb-0 !px-3 !py-2.5 outline-offset-[-1px] bg-n-alpha-black2 placeholder:text-n-slate-10 text-n-slate-12"
      :class="
        hasError
          ? 'outline-n-ruby-8 hover:outline-n-ruby-9 focus:outline-n-ruby-9'
          : 'outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand'
      "
      @input="handleInput"
      @blur="handleBlur"
      @select="rememberCursor"
      @keyup="rememberCursor"
      @click="rememberCursor"
    />

    <Button
      type="button"
      variant="faded"
      color="slate"
      size="md"
      icon="i-lucide-braces"
      :label="t('WHATSAPP_TEMPLATES.PARSER.INSERT_VARIABLE')"
      class="flex-shrink-0"
      @click="toggleDropdown"
    />

    <div
      v-if="isOpen"
      class="absolute right-0 z-50 border rounded-md shadow-lg top-11 w-64 bg-n-solid-1 border-n-strong"
    >
      <ul class="py-1 mb-0 overflow-auto max-h-60" role="listbox">
        <li class="px-3 py-1.5 text-xs font-medium text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.PARSER.INSERT_VARIABLE_TITLE') }}
        </li>
        <li
          v-for="option in nativeOptions"
          :key="option.attr"
          class="flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2 text-n-slate-12"
          role="option"
          @click="insertVariable(option)"
        >
          {{ option.label }}
        </li>

        <template v-if="customOptions.length">
          <li class="px-3 py-1.5 text-xs font-medium text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PARSER.CUSTOM_VARIABLES_TITLE') }}
          </li>
          <li
            v-for="option in customOptions"
            :key="option.attr"
            class="flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2 text-n-slate-12"
            role="option"
            @click="insertVariable(option)"
          >
            {{ option.label }}
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>
